import React, { useState, useRef, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import api from '../api/axios';

// Debounced type-ahead dropdown against /api/users/search (Azure AD via Microsoft Graph),
// reused for both the SCM assignee and eYantra POC email fields. Shows a browsable list
// on focus/click, narrows as you type. Falls back gracefully to plain email entry if the
// search endpoint isn't available (e.g. before the Graph admin consent lands).
// `emailDomain` is optional and opt-in: pass e.g. "@eyantra.com" to hide directory
// results outside that domain. The tenant also contains @eyantraventures.com and
// others, which are valid SCM assignees but must not be offered staff access — so
// the default stays unfiltered and only Team Access narrows it.
export default function ScmAssigneePicker({ value, onChange, disabled, placeholder = 'Search by name or email', onBlur, emailDomain }) {
    const [query, setQuery] = useState(value || '');
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const wrapRef = useRef();

    useEffect(() => { setQuery(value || ''); }, [value]);

    const doSearch = useCallback(
        debounce(async (q) => {
            try {
                // Blank q returns a default browsable list (e.g. on focus, before typing).
                const res = await api.get('/users/search', { params: q ? { q } : {} });
                setResults(res.data || []);
            } catch (err) {
                // Transient/permission failure — clear results silently and let the
                // next keystroke retry; never permanently disable search for this session.
                setResults([]);
            }
        }, 300), []
    );

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        onChange(v);
        setIsOpen(true);
        doSearch(v);
    };

    // Filtered at render rather than inside the debounced fetch, whose closure is
    // created once and would not see a later emailDomain.
    const visibleResults = emailDomain
        ? results.filter(u => String(u.mail || '').toLowerCase().endsWith(emailDomain.toLowerCase()))
        : results;

    const handleSelect = (user) => {
        setQuery(user.mail);
        // Second argument is the full Graph user. Additive — existing callers that only
        // declare (value) are unaffected — and it saves consumers that want to store a
        // display name alongside the email from doing a second lookup for it.
        onChange(user.mail, user);
        setIsOpen(false);
        setResults([]);
    };

    return (
        <div ref={wrapRef} style={{ position: 'relative' }}>
            <input
                type="text"
                className={`qcp-field__input${disabled ? ' qcp-field__input--disabled' : ''}`}
                style={{ paddingRight: '2rem', cursor: disabled ? undefined : 'pointer' }}
                placeholder={placeholder}
                value={query}
                onChange={handleInputChange}
                onFocus={() => { setIsOpen(true); doSearch(query); }}
                onClick={() => { setIsOpen(true); doSearch(query); }}
                onBlur={onBlur}
                disabled={disabled}
            />
            {!disabled && (
                <span style={{
                    position: 'absolute', right: '0.75rem', top: '50%',
                    transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : ''}`,
                    transition: 'transform 0.15s', pointerEvents: 'none', color: '#9ca3af', fontSize: '0.7rem'
                }}>
                    ▼
                </span>
            )}
            {isOpen && visibleResults.length > 0 && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                    marginTop: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxHeight: '220px', overflowY: 'auto'
                }}>
                    {visibleResults.map(u => (
                        <div
                            key={u.id}
                            onClick={() => handleSelect(u)}
                            style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem' }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#f9fafb'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <div style={{ fontWeight: 600, color: '#374151' }}>{u.displayName}</div>
                            <div style={{ color: '#9ca3af' }}>{u.mail}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
